import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { Avatar, Link } from '@mui/material';
import StyledBoxOnHover from '../animated/StyledBoxOnHover';
import { getImageUrl } from '../../utils/utils';
import Favorite from '../Favorite';
import Image from 'next/image';

interface Picture {
  logo?: boolean;
  originalPicturePath?: string;
}

interface Entry {
  parentEntry?: { color?: string };
  icon?: string;
}

interface Actor {
  id: number | string;
  name: string;
  url: string;
  label?: string;
  shortDescription?: string;
  address?: string;
  city?: string;
  entries?: Entry[];
  pictures?: Picture[];
}

interface ActorCardProps {
  actor: Actor;
}

const useStyles = makeStyles<{ color: string; icon: string }>()((theme, { color, icon }) => ({
  '@media print': {
    card: {
      border: 'solid 1px grey',
    },
    favorite: {
      display: 'none !important',
    },
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '10px',
    minHeight: '106px',
    borderLeft: `solid 12px ${color}`,
    display: 'flex',
  },
  content: {
    width: '93%',
    height: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
      margin: '0 24px',
    },
    [theme.breakpoints.down('md')]: {
      margin: '0 5px',
    },
  },
  favorite: {
    [theme.breakpoints.up('sm')]: {
      width: '100px',
    },
    [theme.breakpoints.down('md')]: {
      width: '50px',
    },
    borderLeft: `dashed 2px ${color}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  category: {
    width: '40px',
    height: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  opacity: {
    width: '40px',
    height: '40px',
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: color,
    opacity: '0.3',
  },
  categoryIcon: {
    width: '40px',
    height: '40px',
    mask: `url('/icons/${icon}.svg')`,
    maskPosition: 'center center',
    maskRepeat: 'no-repeat',
    maskSize: '22px',
    background: color,
    borderRadius: '50%',
    opacity: icon ? 1 : 0,
  },
  leftContent: {
    display: 'flex',
    alignItems: 'center',
  },
  image: {
    height: '72px',
    width: '72px',
    [theme.breakpoints.up('sm')]: {
      margin: '0 24px 0 0',
    },
    [theme.breakpoints.down('md')]: {
      margin: '0 5px 0 0',
    },
    '& img': {
      height: '100%',
      width: '100%',
      objectFit: 'contain',
      borderRadius: '50%',
    },
  },
  avatar: {
    backgroundColor: 'white',
  },
  text: {},
  actor: {
    textTransform: 'uppercase',
    color: color,
    fontWeight: 'bold',
  },
  label: {
    color: 'black',
    fontWeight: 'bold',
  },
  location: {
    color: 'black',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.8em',
  },
  actorDetails: {
    fontStyle: 'italic',
    color: '#A3A3A3',
    fontSize: '0.8em',
  },
  icon: {
    color: '#bd0b3d',
    width: '20px',
  },
}));

const ActorCard = ({ actor }: ActorCardProps) => {
  const color = actor?.entries?.[0]?.parentEntry?.color || '#AD2740';
  const icon = actor?.entries?.[0] ? actor.entries[0].icon || 'fruit' : 'fruit';
  const actorName = actor.name;
  const { classes } = useStyles({ color, icon });

  const getActorProfilePicture = (): string | undefined => {
    const profilePictures = actor.pictures?.filter((picture) => picture.logo) || [];
    const picture = profilePictures.length > 0 ? getImageUrl(profilePictures[0].originalPicturePath) : undefined;
    return picture ?? undefined;
  };

  const profilePicture = getActorProfilePicture();

  return (
    <StyledBoxOnHover className={classes.card}>
      <Link href={`/acteur/${actor.url}`} target="_blank" color="inherit" underline="none" width="100%">
        <div className={classes.content}>
          <div className={classes.leftContent}>
            <div className={classes.image}>
              <Avatar
                className={classes.avatar}
                alt={actor.name}
                sx={{
                  width: 72,
                  height: 72,
                }}
              >
                {profilePicture && (
                  <Image src={profilePicture} alt={actor.name} fill style={{ objectFit: 'contain' }} />
                )}
              </Avatar>
            </div>
            <div className={classes.text}>
              <div className={classes.actor}>{actorName}</div>
              <div className={classes.label}>{actor.label}</div>
              <div className={classes.actorDetails}>
                <span>{actor.shortDescription}</span>
              </div>
              {(actor.address || actor.city) && (
                <div className={classes.location}>
                  <img
                    src="/icons/location.svg"
                    alt="Localisation"
                    className={classes.icon}
                  />
                  {(actor.address || '').concat(' ').concat(actor.city || '')}
                </div>
              )}
            </div>
          </div>
          <div className={classes.category}>
            <span className={classes.opacity} />
            <span className={classes.categoryIcon} />
          </div>
        </div>
      </Link>
      <div className={classes.favorite}>
        <Favorite actor={actor} />
      </div>
    </StyledBoxOnHover>
  );
};

export default ActorCard;
